import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  TrendingUp,
  DollarSign,
  Calendar,
  Download,
  AlertCircle,
  RefreshCw,
  Zap,
  CalendarIcon,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { PartnerPortalService, type PartnerReportItem, type PartnerReportSummary } from '@/domain/partner-portal'
import { format, parse } from 'date-fns'
import { th } from 'date-fns/locale'

// Helper to get date range
const getDateRange = (days: number) => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days)
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  }
}

export function AffiliateReports() {
  const [reportData, setReportData] = useState<PartnerReportItem[]>([])
  const [summary, setSummary] = useState<PartnerReportSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [includesRealtime, setIncludesRealtime] = useState(false)

  // Date range state
  const [startDate, setStartDate] = useState(getDateRange(30).start)
  const [endDate, setEndDate] = useState(getDateRange(0).end)
  const [datePreset, setDatePreset] = useState('30days')

  const fetchReport = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true)
    else setIsLoading(true)
    setError(null)

    try {
      const result = await PartnerPortalService.getReport({
        start_date: startDate,
        end_date: endDate,
        include_realtime: true
      })

      if (result.success && result.data) {
        setReportData(result.data.daily || [])
        setSummary(result.data.summary || null)
        setIncludesRealtime(result.data.realtime_included || false)
      } else {
        setError(result.error || 'ไม่สามารถโหลดข้อมูลได้')
      }
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    fetchReport()
  }, [startDate, endDate])

  // Date preset handlers
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset)
    const range = preset === '7days' ? getDateRange(7) :
                  preset === '30days' ? getDateRange(30) :
                  preset === '90days' ? getDateRange(90) :
                  { start: startDate, end: endDate }
    setStartDate(range.start)
    setEndDate(range.end)
  }

  // Top referrals (from report data if available)
  const topDays = useMemo(() => {
    return [...reportData]
      .sort((a, b) => (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0))
      .slice(0, 5)
  }, [reportData])

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg font-medium mb-2">ไม่สามารถโหลดข้อมูลได้</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => fetchReport()}>ลองใหม่</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงาน</h1>
          <p className="text-muted-foreground">สรุปรายได้ตามช่วงเวลา</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchReport(true)} disabled={isRefreshing}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex gap-2">
              <Button 
                variant={datePreset === '7days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('7days')}
              >
                7 วัน
              </Button>
              <Button 
                variant={datePreset === '30days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('30days')}
              >
                30 วัน
              </Button>
              <Button 
                variant={datePreset === '90days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('90days')}
              >
                90 วัน
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {/* Start Date Picker */}
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">จากวันที่</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(parse(startDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : undefined}
                      defaultMonth={startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : new Date()}
                      onSelect={(date) => {
                        if (date) {
                          setStartDate(format(date, 'yyyy-MM-dd'))
                          setDatePreset('custom')
                        }
                      }}
                      locale={th}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {/* End Date Picker */}
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">ถึงวันที่</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(parse(endDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : 'เลือกวันที่'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : undefined}
                      defaultMonth={new Date()}
                      onSelect={(date) => {
                        if (date) {
                          setEndDate(format(date, 'yyyy-MM-dd'))
                          setDatePreset('custom')
                        }
                      }}
                      locale={th}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            {includesRealtime && (
              <Badge variant="secondary" className="bg-primary/10 text-primary gap-1">
                <Zap className="h-3 w-3" />
                รวม Real-time วันนี้
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-blue-500/10">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">รายได้ L1</p>
                  <p className="text-2xl font-bold">{formatCurrency(Number(summary.total_l1) || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-purple-500/10">
                  <TrendingUp className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">รายได้ L2</p>
                  <p className="text-2xl font-bold">{formatCurrency(Number(summary.total_l2) || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-win/10">
                  <DollarSign className="h-5 w-5 text-win" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">รายได้รวม</p>
                  <p className="text-2xl font-bold text-win">{formatCurrency(Number(summary.total_amount) || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-amber-500/10">
                  <Calendar className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">เฉลี่ย/วัน</p>
                  <p className="text-2xl font-bold">{formatCurrency(Number(summary.avg_daily) || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Report Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">รายงานรายวัน</CardTitle>
          <CardDescription>รายได้แยกตามวัน ({reportData.length} วัน)</CardDescription>
        </CardHeader>
        <CardContent>
          {reportData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>ไม่พบข้อมูลในช่วงเวลาที่เลือก</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">วันที่</th>
                    <th className="text-right py-3 px-4">L1</th>
                    <th className="text-right py-3 px-4">L2</th>
                    <th className="text-right py-3 px-4">รวม</th>
                    <th className="text-right py-3 px-4">สมาชิก L1</th>
                    <th className="text-right py-3 px-4">สมาชิก L2</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((data, index) => (
                    <tr key={data.date} className={cn("border-b hover:bg-muted/50", index === 0 && "bg-primary/5")}>
                      <td className="py-3 px-4 font-medium">
                        {data.date}
                        {index === 0 && includesRealtime && (
                          <Badge variant="secondary" className="ml-2 text-[10px] bg-primary/10 text-primary">
                            วันนี้
                          </Badge>
                        )}
                      </td>
                      <td className="text-right py-3 px-4">{formatCurrency(Number(data.l1_amount) || 0)}</td>
                      <td className="text-right py-3 px-4">{formatCurrency(Number(data.l2_amount) || 0)}</td>
                      <td className="text-right py-3 px-4 font-medium text-win">{formatCurrency(Number(data.total_amount) || 0)}</td>
                      <td className="text-right py-3 px-4 text-muted-foreground">{data.l1_members || 0}</td>
                      <td className="text-right py-3 px-4 text-muted-foreground">{data.l2_members || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Days */}
      {topDays.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top 5 วันที่มีรายได้สูงสุด</CardTitle>
            <CardDescription>วันที่ทำรายได้ดีที่สุด</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topDays.map((day, index) => (
                <div key={day.date} className="flex items-center gap-4">
                  <div className={cn(
                    "h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold",
                    index === 0 && "bg-amber-500 text-white",
                    index === 1 && "bg-slate-400 text-white",
                    index === 2 && "bg-orange-600 text-white",
                    index > 2 && "bg-muted text-muted-foreground"
                  )}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{day.date}</p>
                    <p className="text-xs text-muted-foreground">
                      L1: {formatCurrency(Number(day.l1_amount) || 0)} | L2: {formatCurrency(Number(day.l2_amount) || 0)}
                    </p>
                  </div>
                  <p className="font-semibold text-win">{formatCurrency(Number(day.total_amount) || 0)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
